import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import { isFunction, isArray, assign, isUndefined } from 'lodash';
import { format, parse } from 'date-fns';

import { Autocomplete, DateTime } from '~/components/Form';
import { Select } from '~/components/Form/v2';
import { t } from '~/helpers';
import api from '~/services/api';

import * as S from './styles';

export default function Shifts({ forms, onToolbar }) {
  const options = useMemo(
    () => [{ post_desc: 'Todos', post_cod_posto: '' }],
    []
  );

  const { profile } = useSelector((state) => state.user);
  const { token } = useSelector((state) => state.auth);

  const [tank, setTank] = useState({ post_cod_posto: '', post_desc: '' });
  const [initials, setInitials] = useState([]);
  const [finals, setFinals] = useState([]);

  const [dtInitial, setDTInitial] = useState('');
  const [dtFinal, setDTFinal] = useState('');
  const [dtShift, setDTShift] = useState({ ini: '', fin: '' });
  const [dtInput, setDTInput] = useState({ ini: '', fin: '' });

  const handleClearState = useCallback(() => {
    setInitials([]);
    setFinals([]);
    setDTShift({ ini: '', fin: '' });

    if (forms.current && isFunction(forms.current.handleClear)) {
      forms.current.handleClear('dataIni');
      forms.current.handleClear('dataFim');
    }
  }, [forms]);

  const handleChangeTank = useCallback((data) => {
    setTank(data);
    handleClearState();
  }, []);

  const handleGetAPIShifts = useCallback(
    async (query, url, ini = false) => {
      try {
        const { data } = await api.get(`/turno/${url}`, {
          params: assign(query, { codigoposto: tank.post_cod_posto }),
        });

        const response = data.map((item) => ({
          ...item,
          label: `${item.turn_num_turn} - ${
            ini ? item.hora_ini : item.hora_fim
          }`,
          value: ini ? item.turn_datahora_ini : item.turn_datahora_fim,
        }));

        return [
          { label: 'Nenhum', turn_datahora_ini: '', turn_datahora_fim: '' },
          ...response,
        ];
      } catch (err) {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      }
    },
    [tank]
  );

  const handleDateTimeInitials = useCallback(
    async (data) => {
      const dateTime = format(
        parse(data, 'dd/MM/yyyy HH:mm:ss', new Date()),
        'dd/MM/yyyy 00:00:00'
      );

      setDTInitial(dateTime);
      setDTInput((state) => ({ ...state, ini: data }));

      if (forms.current && isFunction(forms.current.handleClear)) {
        forms.current.handleClear('dataIni');
      }

      const query = { datahorainicio: dateTime };

      const response = await handleGetAPIShifts(query, 'getturnosinicio', true);

      setInitials(response);
    },
    [forms, tank]
  );

  const handleDateTimeFinals = useCallback(
    async (data) => {
      const dateTime = format(
        parse(data, 'dd/MM/yyyy HH:mm:ss', new Date()),
        'dd/MM/yyyy 00:00:00'
      );

      setDTFinal(dateTime);
      setDTInput((state) => ({ ...state, fin: data }));

      if (forms.current && isFunction(forms.current.handleClear)) {
        forms.current.handleClear('dataFim');
      }

      const query = { datahorafinal: dateTime };

      const response = await handleGetAPIShifts(query, 'getturnosfim');

      setFinals(response);
    },
    [forms, tank]
  );

  const handleChangeDTShift = useCallback((data, key) => {
    if (!isUndefined(data) && data.value)
      setDTShift((state) => ({ ...state, [key]: data.value }));
  }, []);

  useEffect(() => {
    if (dtInitial) handleDateTimeInitials(dtInitial);
    if (dtFinal) handleDateTimeFinals(dtFinal);
  }, [tank]);

  useEffect(() => {
    const initial = `${dtShift.ini || dtInput.ini}`;
    const final = `${dtShift.fin || dtInput.fin}`;

    onToolbar({
      issuance_date: format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
      period: `${initial} até ${final}`,
      emitter: profile.name,
      username: profile.username,
      tank: tank.post_cod_posto,
      station: tank.post_desc || 'Todos',
      initial,
      final,
      token,
    });
  }, [tank, profile, token, dtShift, dtInput]);

  return (
    <>
      <Autocomplete
        name="post_cod_posto"
        label="post_desc"
        url="usuarioposto"
        unique="post_cod_posto"
        title="Posto"
        hiddens={[{ name: 'post_desc' }]}
        first={options}
        getValue={handleChangeTank}
      />

      <S.Grid columns={2}>
        <DateTime
          time
          name="datahorainicial"
          label="Data/Hora Inicial"
          getValue={handleDateTimeInitials}
        />

        <Select
          name="dataIni"
          label="Turno"
          data={initials}
          getValue={(value) => handleChangeDTShift(value, 'ini')}
        />
      </S.Grid>

      <S.Grid columns={2}>
        <DateTime
          time
          name="datahorafinal"
          label="Data/Hora final"
          getValue={handleDateTimeFinals}
        />

        <Select
          name="dataFim"
          label="Turno"
          data={finals}
          getValue={(value) => handleChangeDTShift(value, 'fin')}
        />
      </S.Grid>
    </>
  );
}

Shifts.propTypes = {
  forms: PropTypes.any.isRequired,
  onToolbar: PropTypes.func,
};

Shifts.defaultProps = {
  onToolbar: () => undefined,
};
